import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getEmailStatus } from '../../../helpers/email.helpers';
import { useLoader } from '../../../helpers/useLoader';
import { InputStatus } from '../../../models/error.model';
import { ProjectRole } from '../../../models/userRole.model';
import { IUserService } from '../../user/user.service';
import { IWorkspaceService } from '../../workspaces/workspaces.service';
import { UsersForm } from '../components/UsersForm';
import { IProjectDetailsBl } from '../projectDetails.bl';
import { useProjectDetailsPermissions } from '../projectDetails.permissions';

export const UsersFormContainer = observer((): ReactElement => {
  const projectDetailsBl = useInjection(IProjectDetailsBl.$);
  const userService = useInjection(IUserService.$);
  const workspaceService = useInjection(IWorkspaceService.$);
  const { withLoaderAsync } = useLoader();
  const { projectId } = useParams<{ projectId: string }>();
  const [email, setEmail] = useState('');
  const [emailState, setEmailState] = useState(InputStatus.empty());
  const [selectedRole, setSelectedRole] = useState(ProjectRole.Annotator);
  const permissions = useProjectDetailsPermissions();

  useEffect(() => {
    if (permissions.canManageUsers().allowed) {
      void workspaceService.getCurrentWorkspaceMembersAsync();
    }
  }, []);

  const handleEmailChange = (email: string): void => {
    setEmail(email);
    setEmailState(InputStatus.empty());
  };

  const handleRoleChange = (role: ProjectRole): void => {
    setSelectedRole(role);
  };

  const validateEmail = (value: string): boolean | undefined => {
    const emailState = getEmailStatus(value, [...projectDetailsBl.store.users.map((u) => u.email)]);
    setEmailState(emailState);
    return emailState.isValid;
  };

  const handleAddNewUser = async (): Promise<void> => {
    if (!validateEmail(email)) {
      return;
    }

    await withLoaderAsync(async (): Promise<void> => {
      await projectDetailsBl.addUserToProjectAsync(projectId, email.trim(), selectedRole);
      await userService.getUserInfoAsync(); // to get user role in project
      setEmail('');
      setEmailState(InputStatus.empty());
    }, 'add-user-button');
  };

  const existingUsersEmails = projectDetailsBl.store.users.map((x) => x.email);
  const users = workspaceService.store.currentWorkspaceMembers.filter((x) => !existingUsersEmails.includes(x.email));

  return (
    <UsersForm
      users={users}
      email={email}
      emailStatus={emailState}
      selectedRole={selectedRole}
      onEmailChange={handleEmailChange}
      onRoleChange={handleRoleChange}
      onAddNewUser={handleAddNewUser}
    />
  );
});
